import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SiteFooter } from "@/components/SiteNav";
import {
  ShieldCheck,
  Landmark,
  Clock,
  BadgeDollarSign,
  Users,
  Building2,
  MailOpen,
  Siren,
  FileCheck2,
  HelpCircle,
  CheckCircle,
  Phone,
  Lock,
  Briefcase,
  Home,
  HandCoins,
  MapPin,
  History,
} from "lucide-react";
import federalIrsIcon from "@/assets/icon-federal-irs.png";
import stateCapitolIcon from "@/assets/icon-state-capitol.png";

// Intuit Commerce Network checkout links, keyed by filer type + coverage.
const CHECKOUT_URLS = {
  individual: {
    federal:
      "https://connect.intuit.com/portal/app/CommerceNetwork/view/scs-v1-798f7298395c417bbb7c4f4b10f457f0604c49ec98f945ca9879c973a1094f834f1863e486cb417c9afe99166760c225-0?locale=EN_US&cta=copylistmultilink",
    state:
      "https://connect.intuit.com/portal/app/CommerceNetwork/view/scs-v1-393713932c934af194add5258577a32adfdd8a566af646cca8b1261a3091bef9749e47729396420383d7b49ab1f6e854-0?locale=EN_US&cta=copylistmultilink",
  },
  business: {
    federal:
      "https://connect.intuit.com/portal/app/CommerceNetwork/view/scs-v1-e4db359229ac4d1a8fba2336bbd22bdaad16a3ce19a04f498f87248980e86615f9fa221781c04ec0919d2b556ca83222-1?locale=EN_US&cta=copylistmultilink",
    state:
      "https://connect.intuit.com/portal/app/CommerceNetwork/view/scs-v1-169b583279844ab99b9098ab5fdcd658be57bc48564e41b399568748fc30ece5c5cade18352e49f9b640ae47b5eadb90-1?locale=EN_US&cta=copylistmultilink",
  },
} as const;

export const Route = createFileRoute("/audit-protection")({
  head: () => ({
    meta: [
      { title: "Nationwide Audit Protection | MyTaxFella" },
      { name: "description", content: "Nationwide IRS and state audit protection from $99 with full EA and CPA representation. Calculate your risk and get coverage in all 50 states." },
      { property: "og:title", content: "Nationwide Audit Protection | MyTaxFella" }, { property: "og:image", content: "https://mytaxfella.com/og-share-2026.jpg" }, { name: "twitter:image", content: "https://mytaxfella.com/og-share-2026.jpg" },
      { property: "og:description", content: "See your live audit savings estimate, then lock in one-time federal & state audit protection from $99, EA & CPA representation included." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://mytaxfella.com/audit-protection" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Nationwide Audit Protection | MyTaxFella" },
      { name: "twitter:description", content: "See your live audit savings estimate, then lock in one-time federal & state audit protection from $99, EA & CPA representation included." },
    ],
    links: [{ rel: "canonical", href: "https://mytaxfella.com/audit-protection" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Sleep Like a Baby™ Audit Protection",
          provider: {
            "@type": "Organization",
            "@id": "https://mytaxfella.com/#organization",
            name: "MyTaxFella",
            url: "https://mytaxfella.com",
            telephone: "+1-718-356-5178",
          },
          description:
            "Nationwide, one-time federal and state tax audit protection with full EA & CPA representation in all 50 states. MyTaxFella responds to IRS and state notices and represents you so you never attend an audit.",
          areaServed: ["New York", "New Jersey", "United States"],
          offers: [
            {
              "@type": "Offer",
              name: "Federal (IRS) Audit Protection",
              price: "124",
              priceCurrency: "USD",
              url: "https://mytaxfella.com/audit-protection",
            },
            {
              "@type": "Offer",
              name: "State Audit Protection",
              price: "99",
              priceCurrency: "USD",
              url: "https://mytaxfella.com/audit-protection",
            },
          ],
        }),
      },
    ],
  }),
  component: AuditProtectionPage,
});

type FilerType = "individual" | "business";
type Coverage = "federal" | "state";
type IrsStatus = "letter" | "audit" | "nothing" | "unsure";

const COVERAGE_PRICES: Record<Coverage, number> = { federal: 124, state: 99 };

const RISK_FACTORS = [
  { id: "self-employed", icon: Briefcase, label: "I'm self-employed or run a business", detail: "Business owners are audited at 2–3× the rate of W-2 employees", weight: 0.2 },
  { id: "home-office", icon: Home, label: "I claimed home office or vehicle deductions", detail: "Common audit triggers, high documentation burden", weight: 0.15 },
  { id: "big-refund", icon: HandCoins, label: "I received a large refund this year", detail: "Refunds over $5,000 draw increased IRS scrutiny", weight: 0.1 },
  { id: "multi-state", icon: MapPin, label: "I had income from multiple states or sources", detail: "Multi-state filers often receive parallel state inquiries", weight: 0.15 },
  { id: "prior-audit", icon: History, label: "I've been audited before", detail: "Prior audit history significantly raises future risk", weight: 0.25 },
] as const;

const IRS_STATUS_OPTIONS: { id: IrsStatus; icon: React.ElementType; label: string; detail: string }[] = [
  { id: "letter", icon: MailOpen, label: "Letter / Notice", detail: "Got a letter or CP notice in the mail" },
  { id: "audit", icon: Siren, label: "Audit Notice", detail: "Told to come in or agent contacted me" },
  { id: "nothing", icon: FileCheck2, label: "Nothing Yet", detail: "Just filed, want to be protected" },
  { id: "unsure", icon: HelpCircle, label: "Not Sure", detail: "I'd like someone to help me figure it out" },
];

const STATUS_MULTIPLIER: Record<IrsStatus, number> = {
  letter: 1.25,
  audit: 1.5,
  nothing: 1,
  unsure: 1.1,
};

function baseExposure(filerType: FilerType, incomeRaw: string): number {
  const income = Number(incomeRaw.replace(/[^0-9.]/g, "")) || 0;
  let base: number;
  if (income < 50000) base = 1400;
  else if (income < 100000) base = 1800;
  else if (income < 200000) base = 2400;
  else if (income < 400000) base = 3200;
  else base = 4200;
  if (filerType === "business") base *= 1.6;
  return base;
}

function estimateExposure(filerType: FilerType, income: string, status: IrsStatus, risks: string[]): number {
  const riskWeight = RISK_FACTORS.filter((r) => risks.includes(r.id)).reduce((sum, r) => sum + r.weight, 0);
  const raw = baseExposure(filerType, income) * STATUS_MULTIPLIER[status] * (1 + riskWeight);
  const rounded = Math.round(raw / 10) * 10;
  return Math.min(Math.max(rounded, 900), 9500);
}

const fmt = (n: number) => `$${n.toLocaleString()}`;

function AuditProtectionPage() {
  return (
    <>
      <main className="pt-[72px] md:pt-20">
        {/* Urgency banner */}
        <div className="bg-green-deep text-cream">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-2.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
            <span className="inline-flex items-center gap-2">
              <Siren size={15} className="text-gold" />
              <span><strong className="text-gold">87,000 new IRS agents</strong> have been hired, audit risk is real</span>
            </span>
            <a href="tel:+17183565178" className="inline-flex items-center gap-1.5 font-semibold text-gold hover:text-cream transition-colors">
              <Phone size={14} /> (718) 356-5178
            </a>
          </div>
        </div>

        {/* Hero */}
        <section className="m-hero service-page-hero section-y">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center m-hero-left">
            <div
              className="m-hero-icon w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 border-2 border-gold shadow-green"
              style={{ background: "var(--gradient-green)" }}
            >
              <ShieldCheck className="text-cream" size={26} strokeWidth={1.5} />
            </div>
            <span className="eyebrow">Sleep Like a Baby™, Audit Protection</span>
            <h1 className="font-display text-4xl md:text-6xl leading-[0.98] mt-4 text-ink">
              What Would an IRS Audit <em className="italic text-green">Cost You?</em>
            </h1>
            <p className="mt-4 text-base md:text-lg font-semibold text-ink leading-snug max-w-2xl mx-auto">
              Nationwide IRS and state audit protection in all 50 states.
            </p>
            <p className="mt-3 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Watch your potential savings calculate live, then lock in coverage in one click.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-2.5">
              {["Federal & State Coverage", "EA & CPA Representation", "One-Time Payment, No Renewals"].map((chip) => (
                <span key={chip} className="inline-flex items-center gap-1.5 rounded-full border border-gold/60 bg-card px-3.5 py-1.5 text-xs md:text-sm font-medium text-ink">
                  <CheckCircle size={14} className="text-green" /> {chip}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Stat strip */}
        <section className="section-y-compact bg-card border-b border-border">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border">
              {[
                { icon: BadgeDollarSign, value: "$3,250+", label: "Avg. Audit Cost Without SLAB" },
                { icon: Clock, value: "40–80 hrs", label: "Client Time Lost in Audit Process" },
                { icon: ShieldCheck, value: "$124", label: "Individual Federal Coverage, One Time" },
                { icon: Users, value: "87K", label: "New IRS Agents Hired" },
              ].map((s) => (
                <div key={s.label} className="bg-card p-4 md:p-6 text-center">
                  <s.icon size={18} className="text-gold mx-auto mb-2 hidden md:block" strokeWidth={1.5} />
                  <div className="font-display text-2xl md:text-3xl text-green">{s.value}</div>
                  <div className="mt-1 text-[11px] md:text-xs text-muted-foreground leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <CalculatorSection />

        {/* Time saved */}
        <section className="section-y bg-background border-b border-border">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
            <div className="text-center mb-8 md:mb-12">
              <span className="eyebrow">Time Saved With SLAB</span>
              <h2 className="section-h2 mt-3">
                You Never Touch the IRS. <em className="italic text-green">We Handle Everything.</em>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: Clock, title: "40–80 hours back", detail: "Calls, letters & paperwork, handled by our EA/CPA team. No sitting on hold with the IRS. No missed work. No stress." },
                { icon: MailOpen, title: "Every letter answered", detail: "We respond to every written request on your behalf, from the first notice to the final resolution." },
                { icon: Landmark, title: "You don't attend", detail: "Field audits, office visits, you don't go. We represent you in front of the IRS and state authorities." },
              ].map((c) => (
                <div key={c.title} className="pc-card pc-service">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: "var(--gradient-green)" }}>
                    <c.icon className="text-cream" size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-lg text-ink">{c.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}

function CalculatorSection() {
  const [filerType, setFilerType] = useState<FilerType>("individual");
  const [coverage, setCoverage] = useState<Coverage[]>(["federal"]);
  const [income, setIncome] = useState("75,000");
  const [irsStatus, setIrsStatus] = useState<IrsStatus>("nothing");
  const [risks, setRisks] = useState<string[]>([]);

  const exposure = useMemo(() => estimateExposure(filerType, income, irsStatus, risks), [filerType, income, irsStatus, risks]);
  const coveragePrice = coverage.reduce((sum, c) => sum + COVERAGE_PRICES[c], 0);
  const savings = Math.max(exposure - coveragePrice, 0);

  // Both selected → send to the federal checkout (higher-tier product).
  const checkoutUrl =
    coverage.length === 0
      ? null
      : CHECKOUT_URLS[filerType][coverage.includes("federal") ? "federal" : "state"];

  const toggleCoverage = (c: Coverage) =>
    setCoverage((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  const toggleRisk = (id: string) =>
    setRisks((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const inputCls =
    "w-full rounded-lg border border-border bg-white px-4 py-3 text-ink placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-green/50";

  return (
    <section className="section-y bg-card border-b border-border" id="calculator">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 lg:gap-10 items-start">
          <div className="space-y-8 md:space-y-10 order-2 lg:order-1">
                {/* Step 1 */}
                <fieldset>
                  <StepHeader number="1" title="Your Situation" detail="Your potential savings update live as you answer" />

                  <p className="text-sm font-medium text-ink mb-3">Who needs protection?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    {([
                      { id: "individual" as FilerType, icon: Users, label: "Individual / Family", detail: "1040 personal returns" },
                      { id: "business" as FilerType, icon: Building2, label: "Business / Corporate", detail: "1120, 1120-S, 1065 returns" },
                    ]).map((o) => (
                      <OptionCard
                        key={o.id}
                        selected={filerType === o.id}
                        onClick={() => setFilerType(o.id)}
                        icon={o.icon}
                        label={o.label}
                        detail={o.detail}
                      />
                    ))}
                  </div>

                  <p className="text-sm font-medium text-ink mb-3">Coverage (select one or both) <span className="text-red-500">*</span></p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    {([
                      { id: "federal" as Coverage, icon: Landmark, iconImg: federalIrsIcon, label: "Federal (IRS)", detail: "Full IRS audit & notice representation", price: "$124 one time" },
                      { id: "state" as Coverage, icon: MapPin, iconImg: stateCapitolIcon, label: "State", detail: "State tax authority audits & inquiries", price: "$99 one time" },
                    ]).map((o) => (
                      <OptionCard
                        key={o.id}
                        selected={coverage.includes(o.id)}
                        onClick={() => toggleCoverage(o.id)}
                        icon={o.icon}
                        iconImg={o.iconImg}
                        label={o.label}
                        detail={o.detail}
                        price={o.price}
                      />
                    ))}
                  </div>

                  <div className="space-y-2 mb-6">
                    <label htmlFor="slab-income" className="text-sm font-medium text-ink">Annual Gross Income (approximate is fine)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <input
                        id="slab-income"
                        inputMode="numeric"
                        value={income}
                        onChange={(e) => setIncome(e.target.value)}
                        placeholder="75,000"
                        className={`${inputCls} pl-8`}
                      />
                    </div>
                  </div>

                  <p className="text-sm font-medium text-ink mb-3">Have you received anything from the IRS?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {IRS_STATUS_OPTIONS.map((o) => (
                      <OptionCard
                        key={o.id}
                        selected={irsStatus === o.id}
                        onClick={() => setIrsStatus(o.id)}
                        icon={o.icon}
                        label={o.label}
                        detail={o.detail}
                      />
                    ))}
                  </div>
                </fieldset>

                {/* Step 2 */}
                <fieldset>
                  <StepHeader number="2" title="Additional Risk Factors" detail="Check anything that applies, this affects your exposure estimate" />
                  <div className="grid grid-cols-1 gap-3">
                    {RISK_FACTORS.map((r) => (
                      <OptionCard
                        key={r.id}
                        selected={risks.includes(r.id)}
                        onClick={() => toggleRisk(r.id)}
                        icon={r.icon}
                        label={r.label}
                        detail={r.detail}
                      />
                    ))}
                  </div>
                </fieldset>

                {/* CTA */}
                <div className="space-y-3">
                  {checkoutUrl ? (
                    <a
                      href={checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-green w-full inline-flex items-center justify-center gap-2"
                    >
                      <Lock className="w-4 h-4" />
                      Protect Me Now, Secure Checkout →
                    </a>
                  ) : (
                    <p className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                      Please select at least one coverage option (Federal and/or State).
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground text-center">
                    Secure checkout, you'll enter your details on the payment page.
                  </p>
                </div>
          </div>

          {/* Live savings panel */}
          <aside className="order-1 lg:order-2 lg:sticky lg:top-24">
            <div className="rounded-2xl border-2 border-gold bg-card overflow-hidden shadow-green">
              <div className="bg-green-deep text-cream px-5 py-4 text-center">
                <span className="eyebrow !text-gold">Your Live Potential Savings</span>
              </div>
              <div className="p-5 md:p-6 space-y-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-muted-foreground">Estimated audit exposure without SLAB</span>
                  <span className="font-display text-xl text-ink whitespace-nowrap">{fmt(exposure)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-muted-foreground">
                    Your SLAB coverage, {coverage.length === 1 ? "1 product" : coverage.length === 2 ? "2 products" : "no product"} (one-time)
                  </span>
                  <span className="font-display text-xl text-ink whitespace-nowrap">{coveragePrice ? fmt(coveragePrice) : ", "}</span>
                </div>
                <div className="hairline" />
                <div className="text-center py-2">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Potential Savings</div>
                  <div className="font-display text-5xl md:text-6xl text-green mt-1 transition-all">{fmt(savings)}</div>
                  <div className="mt-1 text-xs text-muted-foreground">+ up to 40–80 hours of your time back</div>
                </div>
                {checkoutUrl && (
                  <a
                    href={checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-green w-full inline-flex items-center justify-center gap-2"
                  >
                    <Lock className="w-4 h-4" />
                    Protect Me Now →
                  </a>
                )}
              </div>
              <p className="px-5 pb-5 text-[10px] leading-relaxed text-muted-foreground">
                Disclaimer: Figures shown are estimates for illustration only, based on industry averages for professional audit representation and the information you entered. They are not a quote of actual audit costs, penalties, or tax liability, and are not a guarantee of savings or outcome. Actual costs vary by case complexity, taxing authority, and scope of examination. SLAB coverage terms, conditions, and exclusions apply. This is not tax or legal advice.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function StepHeader({ number, title, detail }: { number: string; title: string; detail: string }) {
  return (
    <div className="flex items-start gap-3 mb-4 md:mb-5">
      <span
        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-display text-sm text-cream"
        style={{ background: "var(--gradient-green)" }}
      >
        {number}
      </span>
      <div>
        <h2 className="font-display text-xl md:text-2xl text-ink leading-tight">{title}</h2>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">{detail}</p>
      </div>
    </div>
  );
}

function OptionCard({
  selected,
  onClick,
  icon: Icon,
  iconImg,
  label,
  detail,
  price,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ElementType;
  iconImg?: string;
  label: string;
  detail: string;
  price?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`relative text-left rounded-xl border px-4 py-3.5 transition-colors focus:outline-none focus:ring-2 focus:ring-green/50 ${
        selected ? "border-green bg-green/10" : "border-border bg-white hover:border-gold/70"
      }`}
    >
      <span className="flex items-start gap-3">
        <span
          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            selected ? "bg-green text-cream" : "bg-green/10 text-green"
          }`}
        >
          {iconImg ? (
            <img
              src={iconImg}
              alt=""
              aria-hidden="true"
              loading="lazy"
              width={22}
              height={22}
              className="h-[22px] w-[22px] object-contain"
              style={selected ? { filter: "brightness(0) invert(1)" } : undefined}
            />
          ) : (
            <Icon size={18} strokeWidth={1.5} />
          )}
        </span>
        <span className="min-w-0">
          <span className="flex items-center gap-2 text-sm font-semibold text-ink">
            {label}
            {selected && <CheckCircle size={15} className="text-green shrink-0" />}
          </span>
          <span className="block text-xs text-muted-foreground mt-0.5 leading-snug">{detail}</span>
          {price && <span className="block text-xs font-semibold text-gold mt-1">{price}</span>}
        </span>
      </span>
    </button>
  );
}
